import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ProfileProvider } from './context/ProfileContext';
import Navbar         from './components/layout/Navbar';
import Footer         from './components/layout/Footer';
import SearchOverlay  from './components/ui/SearchOverlay';
import AppInstallBanner from './components/ui/AppInstallBanner';
import ErrorBoundary  from './components/ui/ErrorBoundary';
import NetworkStatus  from './components/ui/NetworkStatus';
import { ToastProvider } from './components/ui/Toast';
// HomePage stays eager — it's the most common landing page, and lazy-loading
// it would add a visible Suspense flash on the very first paint most visitors see.
import HomePage       from './pages/HomePage';
// Everything else is lazy: each page's code (and, critically, VideoPlayerPage's
// hls.js dependency — 500KB+ on its own) only downloads when someone actually
// navigates there, instead of shipping in the one bundle everyone pays for
// just to see the homepage.
const MoviesPage      = lazy(() => import('./pages/MoviesPage'));
const SeriesPage      = lazy(() => import('./pages/SeriesPage'));
const ShowDetailPage  = lazy(() => import('./pages/ShowDetailPage'));
const VideoPlayerPage = lazy(() => import('./pages/VideoPlayerPage'));
const MyListPage      = lazy(() => import('./pages/MyListPage'));
const PlansPage       = lazy(() => import('./pages/PlansPage'));
const ProfilePage     = lazy(() => import('./pages/ProfilePage'));
const AboutPage       = lazy(() => import('./pages/AboutPage'));
const PrivacyPage     = lazy(() => import('./pages/PrivacyPage'));
const TermsPage       = lazy(() => import('./pages/TermsPage'));
const BrowsePage      = lazy(() => import('./pages/BrowsePage'));
const FaqPage         = lazy(() => import('./pages/FaqPage'));
const ContactPage     = lazy(() => import('./pages/ContactPage'));
import './styles/global.css';

// ── Minimal route-load fallback — brief, unobtrusive, matches dark theme ──
function RouteFallback() {
  return (
    <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="sf-spin-small" />
    </div>
  );
}

// ── Global scroll-to-top on every route change ────────────────────────────
function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname]);
  return null;
}

function AppShell() {
  const location = useLocation();
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    const handler = () => setSearchOpen(true);
    window.addEventListener('sf:open-search', handler);
    return () => window.removeEventListener('sf:open-search', handler);
  }, []);
  const isPlayerPage = location.pathname.startsWith('/watch/');

  return (
    <>
      <NetworkStatus />
      {!isPlayerPage && <AppInstallBanner />}
      {!isPlayerPage && <Navbar onSearchOpen={() => setSearchOpen(true)} />}
      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />

      <ScrollToTop />
      <main className={isPlayerPage ? 'main-player' : 'main-content'}>
        <ErrorBoundary>
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/"           element={<ErrorBoundary inline><HomePage /></ErrorBoundary>} />
            <Route path="/movies"     element={<ErrorBoundary inline><MoviesPage /></ErrorBoundary>} />
            <Route path="/series"     element={<ErrorBoundary inline><SeriesPage /></ErrorBoundary>} />
            <Route path="/show/:id"   element={<ErrorBoundary inline><ShowDetailPage /></ErrorBoundary>} />
            <Route path="/watch/:showId/:videoId" element={<ErrorBoundary inline><VideoPlayerPage /></ErrorBoundary>} />
            <Route path="/mylist"     element={<ErrorBoundary inline><MyListPage /></ErrorBoundary>} />
            <Route path="/subscription" element={<ErrorBoundary inline><PlansPage /></ErrorBoundary>} />
            {/* Legacy path redirects — /plans was the previous canonical path
                (still referenced by some old bookmarks/links); /subscribe is
                a common alternate spelling some app builds may use. Both
                just forward straight to the real page. */}
            <Route path="/plans"        element={<Navigate to="/subscription" replace />} />
            <Route path="/subscribe"    element={<Navigate to="/subscription" replace />} />
            <Route path="/profile"    element={<ErrorBoundary inline><ProfilePage /></ErrorBoundary>} />
            <Route path="/browse"     element={<ErrorBoundary inline><BrowsePage /></ErrorBoundary>} />
            <Route path="/about"      element={<ErrorBoundary inline><AboutPage /></ErrorBoundary>} />
            <Route path="/privacy"    element={<ErrorBoundary inline><PrivacyPage /></ErrorBoundary>} />
            <Route path="/terms"      element={<ErrorBoundary inline><TermsPage /></ErrorBoundary>} />
            <Route path="/faq"        element={<ErrorBoundary inline><FaqPage /></ErrorBoundary>} />
            <Route path="/contact"    element={<ErrorBoundary inline><ContactPage /></ErrorBoundary>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
        </ErrorBoundary>
      </main>
      {!isPlayerPage && <Footer />}
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ProfileProvider>
          <ToastProvider>
            <AppShell />
          </ToastProvider>
        </ProfileProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
